
/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Container, Modal } from "react-bootstrap";
import { QueryListProvider } from "../../../../component/solution/QueryTableList/QueryListContext";
import { QueryTableList } from "../../../../component/solution/QueryTableList/QueryTableList";
import { formatDateFull } from "../../../../component/solution/utils/AppUtil";
import { IntegrationStatus, IQueryTableData } from "../../../../component/solution/Interfaces";
import { useAuth } from "../../../../contexts/AuthContext";
import { AlertTriangle, Ban, Bell } from "lucide-react";


export const StockMovementOut: React.FC = () => {

    const { fetchData, userDetail, setRefreshTable } = useAuth();
    const navigate = useNavigate();
        
    const [showModal, setShowModal] = useState(false);
    const [modalLayout, setModalLayout] = useState<React.ReactNode | null>(null);
    const [modalData, setModalData] = useState<{ modalTitle: string; modalSubtitle: string } | null>(null);

    const [showCancelTransactionModal, setShowCancelTransactionModal] = useState<boolean>(false);
    const [cancelledTransactionData,  setCancelledTransactionData] = useState<{ 
        user_id: string, 
        document_number: string, 
        document_type: string, 
        transaction_number: string, 
        title: string
    } | null>(null);


    const openModal = (docNum: string) => {
        fetchDetailedSummary(docNum); 
    }
       
    const clearModalHistory = () => {
        setShowModal(false); 
        setModalLayout(null); 
        setModalData(null);
    }
       
    const fetchDetailedSummary = async (docNum: string) => {
                
        const querySummaryUrl = `${process.env.REACT_APP_BASE_URL}/table/sa_m24l_stock_movement_out_line/data?includeCount=true`;
    
        const tableSummaryQuery: IQueryTableData = {
            tableName: "sa_m24l_stock_movement_out_line",
            where: 'doc_num = :doc_num',
            orderBy: 'item_code ASC',
            pageNum: 0,
            pageSize: 0,
            params: { 'doc_num': `${docNum}`}
        }; 

        fetchData(querySummaryUrl, {method:"POST", body: JSON.stringify(tableSummaryQuery)}, (result: any) =>  processSummary(result, docNum));
        setShowModal(true);
    }
       
    
    // Process the Result into a table for display purposes.
    const processSummary = (result: any, docNum: string) => {

        const detail = result.data;
        const { summaryTable, detailTable } = generateDetailTables(detail);

        // Set the layout for the modal
        setModalLayout(
             <div className="d-flex flex-column gap-3" >
                <div style={{background:"#f2f5f3"}} className="p-2">
                    <h6 className="text-secondary fw-bolder">
                        Summary
                    </h6>
                    <div className="mb-3">{summaryTable}</div>
                </div>

                <div style={{background:"#f2f5f3"}} className="p-2">
                    <h6 className="text-secondary fw-bolder">
                        Line Details
                    </h6>
                    <table className="table" style={{ border: '1px solid #adb5bd' }}>
                        <thead>
                            <tr>
                                <th style={{ border: '1px solid #adb5bd' }}>Line No.</th>
                                <th style={{ border: '1px solid #adb5bd' }}>Item Code</th>
                                <th style={{ border: '1px solid #adb5bd' }}>From Warehouse</th>
                                <th style={{ border: '1px solid #adb5bd' }}>To Warehouse</th>
                                <th style={{ border: '1px solid #adb5bd' }}>Qty Ordered</th>
                                <th style={{ border: '1px solid #adb5bd' }}>Qty Sent</th>
                                <th style={{ border: '1px solid #adb5bd' }}>Qty Received</th>
                            </tr>
                        </thead>
                        <tbody>{detailTable}</tbody>
                    </table>
                </div>
            </div>
        );

        setModalData({ modalTitle: `Doc Num: [${docNum}]`, modalSubtitle: `Outbound - Retail:` });
        setShowModal(true);
    };
    
    
 
    const generateDetailTables = (detail: any[]) => {
        const detailTable: React.ReactElement[] = [];
        let totalQuantityOrdered = 0;
        let totalQuantityReceived = 0;
        let totalQuantitySent = 0;

        // Sort by line_num ascending before rendering
        const sortedDetail = [...detail].sort((a, b) => {
            const aLine = a.data?.line_num ?? 0;
            const bLine = b.data?.line_num ?? 0;
            return aLine - bLine;
        });

        sortedDetail.forEach((item, idx) => {
            const data = item.data;
            totalQuantityOrdered += data?.qty_ordered ?? 0;
            totalQuantityReceived += data?.qty_received ?? 0;
            totalQuantitySent += data?.qty_sent ?? 0;

            detailTable.push(
            <tr key={idx}>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.line_num}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.item_code}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.from_warehouse}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.to_warehouse}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.qty_ordered}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.qty_sent}</td>
                <td className="text-center" style={{ border: '1px solid #adb5bd' }}>{data?.qty_received}</td>
            </tr>
            );
        });

        const summaryTable = (
           <table className="table w-auto" style={{ border: '1px solid #adb5bd' }}>
            <tbody>
                <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Unique Item Total</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{detail.length}</td>
                </tr>
                 <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Total Qty Ordered</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{totalQuantityOrdered}</td>
                </tr>
                 <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Total Qty Sent</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{totalQuantitySent}</td>
                </tr>
                 <tr>
                    <th style={{ width: '150px', border: '1px solid #adb5bd' }}>Total Qty Received</th>
                    <td style={{ width: '100px',  border: '1px solid #adb5bd' }} className="text-end">{totalQuantityReceived}</td>
                </tr>
            </tbody>
        </table>
        );

        return { summaryTable, detailTable };
    };

    /*
    Status 

    Outbound - Retail
    TO_SEND - Document ready to be sent to M24L.
    SENT - Doument has has been sent to M24L for processing.
    SUCCESS - document successfully received at M24L for processing.
    ERROR - There was a problem integrating the document at M24L.
    PICKING - M24L have started picking the stock.
    DISPATCHED - Stock dispatched from M24L.
    TO_CLOSE - Stock has been received at the store and pending update to SAP.
    CLOSED - Document processing complete.

    */

     /* 
      [Table Rows & Order To Display]: 
        doc_num  
        division  
        card_code  
        card_name  
        num_at_card  
        from_warehouse  
        to_warehouse  
        integration_status  
        total_qty  
        comments  
        created  
        updated  
    */

    const getStatusColor = (status: string): { backgroundColor: string; color: string; } | {} => {
            if (IntegrationStatus[status as keyof typeof IntegrationStatus]) {
                return { backgroundColor: IntegrationStatus[status as keyof typeof IntegrationStatus], color: 'white' };
            }
            return {};
    };

    //CANCEL TRASCATION SECTION 
    const openModalValidTransaction = (user_id: string, document_number: string, document_type: string, transactionNumber: string, title: string) => {
        if (user_id === "" || document_number === "" || document_type === "") return;
        setCancelledTransactionData({ user_id: user_id, document_number: document_number, document_type: document_type, transaction_number: transactionNumber, title: title}); 
        setShowCancelTransactionModal(true); 
    }


    //handle canceld transaction
    const handleCancelTransaction = (user_id: string, document_number: string, document_type: string, transaction_number: string) => {

        if (user_id === "" || document_number === "" || document_type === "") return;
 
        // form data to be posted
        const formData  = { 
            user_id: user_id, 
            document_number: document_number, 
            document_type: document_type, 
            transaction_number: transaction_number 
        };

        fetchData(`${process.env.REACT_APP_BASE_URL}/functions/CANCEL-DOCUMENT/run`, 
        {method:"POST", body: JSON.stringify(formData)}, 
        (result: any, status: string) =>  { 
            if(status === "200"){
              setRefreshTable(prev => !prev);
            }
        });
    }

    const clearCancelTransactionModal = () => {
        setShowCancelTransactionModal(false); 
        setCancelledTransactionData(null);
    }


    return(
        <React.Fragment>
            <Container fluid className="p-0">
            <QueryListProvider>
                    <QueryTableList
                        listTitle={"Outbound - Retail"}
                        recordTitle={"Outbound - Retail"}
                        tableName={"sa_m24l_stock_movement_out"}
                        editTableName={"sa_m24l_stock_movement_out"}
                        hasIntegrationStatusLegend={true} 
                        errorCategoryName={'MovementOut'}
                        integrationstatusLegend={[
                            { status: 'ERROR', color: '#bf3636', description: 'There was a problem integrating the document at M24L.'},
                            { status: 'CANCELLED', color: '#8b8b8c', description: 'Document has been cancelled.'},
                            { status: 'TO_SEND', color: '#d4a53f', description: 'Document ready to be sent to M24L.'},
                            { status: 'PICKING', color: '#d4a53f', description: 'M24L have started picking the stock.'},
                            { status: 'DISPATCHED', color: '#d4a53f', description: 'Stock dispatched from M24L.'},
                            { status: 'SENT', color: '#d4a53f', description: 'Document has been sent to M24L for processing.'},
                            { status: 'SUCCESS', color: '#d4a53f', description: 'Document successfully received at M24L for processing.'},
                            { status: 'TO_CLOSE', color: '#d4a53f', description: 'Stock has been received at the store and pending update to SAP.'},
                            { status: 'CLOSED', color: '#3ec76b', description: 'Document processing complete.'}
                        ]}
                        editHasCompoundKey={false}   
                        showFailedTransactionsTable={false}
                        query={{
                            appCode: "ares",
                            selectCode: "stock-movement-out-notification-projection",
                            tableName: null,
                            where: "",
                            orderBy: "",
                            pageNum: 0,
                            pageSize: 100,
                            params: {},
                        }} 
                        orderColumns={[
                            { name: "doc_num", type: "INTEGER", sortable: true, searchable: true },
                            { name: "division", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "card_code", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "card_name", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "num_at_card", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "from_warehouse", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "to_warehouse", type: "INTEGER", sortable: true, searchable: true },
                            { name: "integration_status", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "total_qty", type: "INTEGER", sortable: true, searchable: true },
                            { name: "comments", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "created", type: "TIMESTAMP", sortable: true, searchable: false },
                            { name: "updated", type: "TIMESTAMP", sortable: true, searchable: false }
                        ]}
                        reportOptions={{ 
                             reportQuery: {
                              appCode: "ares",
                              selectCode: "stock-movement-out-with-line",
                              tableName: null, 
                              where: "",
                              orderBy: "",
                              pageNum: 0,
                              pageSize: 0,
                              params: {},
                            },
                            templateName: "apps/ares/stock_movement_out_template.xlsx", 
                            reportName: "stock_movement_out_report.xlsx",
                            reportData: null
                        }}  

                        renderRowData={(row) => {
                                                
                            const tdStyle: React.CSSProperties = { whiteSpace: 'nowrap', border: '1px solid #deebed',  padding: '4px 8px', fontSize: '0.75rem' };
                            const statusColor = getStatusColor(row.integration_status); 
                            const tdEllipsis: React.CSSProperties = {...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}
                            const tdHasDetailsOption: React.CSSProperties = { ...tdStyle,  textDecoration: 'underline', cursor: 'pointer'};

                            return (
                                <>
                                    <td style={tdHasDetailsOption} title={row.doc_num}>
                                        <span onClick={() => openModal(row.doc_num)}>{row.doc_num}</span>
                                    </td>
                                    <td style={tdStyle} title={row.division}>{row.division}</td>
                                    <td style={tdStyle} title={row.card_code}>{row.card_code}</td>
                                    <td style={tdStyle} title={row.card_name}>{row.card_name}</td>
                                    <td style={tdStyle} title={row.num_at_card}>{row.num_at_card}</td>
                                    <td style={tdStyle} title={row.from_warehouse}>{row.from_warehouse}</td>
                                    <td style={tdStyle} title={row.to_warehouse}>{row.to_warehouse}</td>
                                    <td style={{...tdStyle,...statusColor, textAlign: "center", verticalAlign: "middle"}} title={row.integration_status}>
                                        {row.integration_status}
                                    </td>
                                    <td style={tdStyle} title={row.total_qty}>{row.total_qty}</td>
                                    <td style={tdEllipsis} title={row.comments}>{row.comments}</td>
                                    <td style={tdStyle} title={formatDateFull(String(row.created))}>{formatDateFull(String(row.created))}</td>
                                    <td style={tdStyle} title={formatDateFull(String(row.updated))}>{formatDateFull(String(row.updated))}</td>
                                </>    
                            );
                        }}
                        renderActions={(row) => {

                            //checking has error notification
                            const numericRefValue = Number(row.num_notifications);
                            const shouldShowErrorButton = numericRefValue > 0 && row.integration_status !== "CLOSED";

                            //checking last updated 
                            const updatedAtValue = row.updated; 
                            const createdAtValue = row.created;
                            const baseDateStr = typeof updatedAtValue === "string" && updatedAtValue.trim() ? updatedAtValue : typeof createdAtValue === "string" && createdAtValue.trim() ? createdAtValue : null;
                            const baseDate = baseDateStr ? new Date(baseDateStr).getTime() : null;
                            const isStaleUpdate = baseDate !== null && row.integration_status !== "CLOSED" && Date.now() - baseDate > 12 * 60 * 60 * 1000; // 12 hours

                            //can cancel transaction  
                            const hasCancelPermission = userDetail?.roles?.includes("cancel_documents");
                            const invalidCancelStatuses = ["CLOSED", "CANCELLED", "ERROR", "PROCESSED"];
                            const hasStatusCancelPermission = !invalidCancelStatuses.includes(String(row.integration_status));
                            const canCancelTransaction = hasCancelPermission && row.doc_num && hasStatusCancelPermission;

                            return (
                                 <td className="sa-record-control" style={{ border: '1px solid #deebed', height: '45px', fontSize: '0.55rem', padding: '0 5px'}}>
                                    <div className="d-flex gap-2">

                                    {shouldShowErrorButton && row.doc_num && (
                                        <button className="btn btn-light position-relative" title="View On Error Notifications"
                                            onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            navigate('/m24l/notifications-new', { state: { shipment_num: row.doc_num as string, category: 'MovementOut'}});
                                            }}
                                            style={{ width: '32px', height: '32px', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', border:'1px solid #dbdad9' }}
                                        >
                                            <span className="position-absolute top-0 end-0" style={{ fontSize: "0.60rem" , paddingRight: '2px', color: '#f7633e', fontWeight:'600' }}>
                                                {numericRefValue}
                                            </span>
                                            <Bell style={{ width: "15px", height: "15px", color:'#343a40', position: 'absolute', top: '60%', transform: 'translateY(-6px)' }} />
                                        </button>
                                    )}

                                    {isStaleUpdate && row.doc_num && (
                                        <button className="btn btn-light" title="Last update was over 12 hours ago"
                                            style={{ width: '32px', height: '32px', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', border: '1px solid #dbdad9' }}
                                        >
                                            <AlertTriangle color="#4d1302" size={16} />
                                        </button>
                                    )}

    
                                    {canCancelTransaction && row.doc_num && (
                                    <button className="btn btn-light" title="Cancel Transaction"
                                            onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            openModalValidTransaction( userDetail?.id ?? "", String(row.doc_num), 'stock_movement_out', String(row.transaction_num ?? ""), "Outbound - Retail")
                                            }}
                                            style={{ width: '32px', height: '32px', padding: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', border: '1px solid #dbdad9' }}
                                        >
                                            <Ban color="#4d1302" size={16} />
                                    </button>
                                    )}
                                </div>   
                            </td>
                            );
                        }}     
                    />
                </QueryListProvider>

                { /* open modal detailed view [start] */ }
                <Modal show={showModal} onHide={clearModalHistory} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{`${modalData?.modalTitle}`}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <h5 className="text-uppercase text-secondary fw-bolder pb-1">{modalData?.modalSubtitle}</h5>
                        {modalLayout}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={clearModalHistory}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
                { /* open modal detailed view [end] */ }

                 { /* open modal cancel transaction [start] */ }
                <Modal show={showCancelTransactionModal} onHide={clearCancelTransactionModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>{ cancelledTransactionData?.title }: [ { cancelledTransactionData?.document_number } ]</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                        <div className="informational-message-section">
                            <p>
                                <strong>Note:</strong> You are about to <strong>cancel this transaction</strong>. 
                                Clicking <strong>Confirm</strong> will permanently cancel the record and any associated processes. 
                                Closing this modal or clicking <strong>Cancel</strong> will <strong>abort the cancellation</strong> and leave the transaction unchanged.
                            </p>
                        </div>
                        </Modal.Body>
                        <Modal.Footer>
                        <Button variant="primary" onClick={() => { 
                            setShowCancelTransactionModal(false); 
                            handleCancelTransaction(
                                cancelledTransactionData?.user_id ?? "", 
                                cancelledTransactionData?.document_number ?? "", 
                                cancelledTransactionData?.document_type ?? "",
                                cancelledTransactionData?.transaction_number ?? ""
                            ); 
                            setCancelledTransactionData(null);
                        }}
                        >
                            Confirm
                        </Button>
                        <Button variant="secondary" onClick={clearCancelTransactionModal}>
                            Cancel
                        </Button>
                        </Modal.Footer>
                  </Modal>
                { /* open modal open modal cancel transaction [end] */ }
                                

            </Container>
        </React.Fragment>
    );
}